import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AgGridReact } from '@ag-grid-community/react';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import './AdminDashboard.css';

const EXPERIMENTS_URL = '/server/experiments';

const RECORD_TYPE_OPTIONS = ['CRM Quote', 'CRM SO', 'Books SO', 'Books Invoice', 'Books Retainer'];

// ─── Action definitions ──────────────────────────────────────────────
// Each action maps to an experiments endpoint the UI can call
const ACTION_DEFS = [
  {
    id: 'sync-internal-users',
    title: 'Sync Internal Users',
    desc: 'Sync CRM users → InternalUsers table (Active, Role). Replaced_By_ID is preserved.',
    method: 'ALL',
    path: '/sync-internal-users',
    params: [],
  },
  {
    id: 'bulk-presync',
    title: '⚡ Bulk Pre-Sync',
    desc: 'COQL bulk loader — syncs headers directly to DataStore. No jobs, no Books writes. Items load on-demand in portal.',
    method: 'ALL',
    path: '/bulk-presync',
    params: [
      { key: 'dryRun', label: 'Dry run', type: 'bool', default: true },
      { key: 'delayMs', label: 'Delay (ms)', type: 'number', default: 100 },
      { key: 'limit', label: 'Limit accounts', type: 'number', default: '' },
      { key: 'skipSynced', label: 'Skip synced', type: 'bool', default: true },
    ],
  },
  {
    id: 'check-token-scopes',
    title: 'Check Token Scopes',
    desc: 'Show currently granted Zoho access token scopes.',
    method: 'GET',
    path: '/check-token-scopes',
    params: [],
  },
  {
    id: 'books-bulk-sync',
    title: 'Books Bulk Sync',
    desc: 'Re-sync all Books SOs/Invoices/Retainer Invoices via job pool.',
    method: 'ALL',
    path: '/books-bulk-sync',
    params: [
      { key: 'dryRun', label: 'Dry run', type: 'bool', default: true },
    ],
  },
  {
    id: 'books-presync',
    title: '📚 Books Pre-Sync',
    desc: 'Inline sync all Books SOs/Invoices/Retainer Invoices → DataStore (incl. PDF download + Stratus upload). Auto-batches.',
    method: 'ALL',
    path: '/books-presync',
    autoBatch: true, // enable auto-batch loop
    params: [
      { key: 'dryRun', label: 'Dry run', type: 'bool', default: true },
      { key: 'modules', label: 'Modules', type: 'text', default: 'salesorders,invoices,retainerinvoices' },
      { key: 'skipPdf', label: 'Skip PDF', type: 'bool', default: false },
      { key: 'limit', label: 'Batch size', type: 'number', default: 10 },
      { key: 'delayMs', label: 'Delay (ms)', type: 'number', default: 50 },
    ],
  },
  {
    id: 'countries-seed',
    title: 'Seed Countries',
    desc: 'Seed/update the Countries DataStore table from built-in data.',
    method: 'ALL',
    path: '/countries/seed',
    params: [],
  },
  {
    id: 'portal-state-config-seed',
    title: 'Seed Portal State Config',
    desc: 'Seed/reset PortalStateConfig table — maps CRM Stage/Status → Portal_State + visibility.',
    method: 'ALL',
    path: '/portal-state-config/seed',
    params: [],
  },
];

// ─── Action Card Component ──────────────────────────────────────────
function ActionCard({ def }) {
  const [paramValues, setParamValues] = useState(() => {
    const init = {};
    (def.params || []).forEach(p => { init[p.key] = p.default; });
    return init;
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultStatus, setResultStatus] = useState(null); // 'success' | 'error'
  const [batchLog, setBatchLog] = useState([]); // for auto-batch mode
  const abortRef = useRef(false);

  const handleParamChange = (key, value, type) => {
    setParamValues(prev => ({
      ...prev,
      [key]: type === 'bool' ? value === 'true' : type === 'number' ? value : value,
    }));
  };

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    setResultStatus(null);
    setBatchLog([]);
    abortRef.current = false;

    const method = def.method === 'ALL' ? 'POST' : def.method;

    if (def.autoBatch) {
      // Auto-batch loop: call with offset, increment until hasMore=false
      let currentOffset = 0;
      const accumulatedCounters = {};
      const accumulatedErrors = [];
      let totalIds = null;
      let batchNum = 0;

      try {
        while (!abortRef.current) {
          batchNum++;
          const bn = batchNum; // capture for closures
          const co = currentOffset;
          const qs = new URLSearchParams();
          Object.entries(paramValues).forEach(([k, v]) => {
            if (v !== '' && v !== undefined && v !== null) qs.set(k, String(v));
          });
          qs.set('offset', String(co));

          const url = `${EXPERIMENTS_URL}${def.path}?${qs.toString()}`;
          setBatchLog(prev => [...prev, `⏳ Batch #${bn} (offset=${co})...`]);

          const resp = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' } });
          const data = await resp.json().catch(() => null);

          if (!resp.ok || !data?.success) {
            const errMsg = data?.error || `HTTP ${resp.status}`;
            setBatchLog(prev => [...prev, `❌ Batch #${bn} failed: ${errMsg}`]);
            setResultStatus('error');
            break;
          }

          // Accumulate
          if (totalIds === null) totalIds = data.totalIds;
          const tid = totalIds; // capture
          if (data.counters) {
            for (const [k, v] of Object.entries(data.counters)) {
              accumulatedCounters[k] = (accumulatedCounters[k] || 0) + v;
            }
          }
          if (data.errors?.length) accumulatedErrors.push(...data.errors);

          const processedItems = (data.processed || []).filter(p => p.status === 'ok').length;
          const errorItems = (data.processed || []).filter(p => p.status === 'error').length;
          const pdfItems = (data.processed || []).filter(p => p.pdf === 'yes').length;
          const line = `✅ Batch #${bn}: ${processedItems} ok${errorItems ? `, ${errorItems} errors` : ''}${pdfItems ? `, ${pdfItems} PDFs` : ''} (${data.elapsed})`;
          setBatchLog(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = line;
            return updated;
          });

          if (!data.hasMore) {
            setBatchLog(prev => [...prev, `🏁 Done — ${tid} total, processed all.`]);
            setResultStatus('success');
            toast.success(`${def.title} completed (${tid} records)`);
            break;
          }

          currentOffset = data.nextOffset;
        }

        if (abortRef.current) {
          setBatchLog(prev => [...prev, `⛔ Stopped by user at offset ${currentOffset}`]);
          setResultStatus('error');
        }

        setResult(JSON.stringify({ totalIds, counters: accumulatedCounters, errors: accumulatedErrors.slice(0, 30) }, null, 2));
      } catch (err) {
        setBatchLog(prev => [...prev, `❌ Error: ${err.message}`]);
        setResult(err.message);
        setResultStatus('error');
        toast.error(`${def.title}: ${err.message}`);
      } finally {
        setRunning(false);
      }
    } else {
      // Standard single-shot call
      const qs = new URLSearchParams();
      Object.entries(paramValues).forEach(([k, v]) => {
        if (v !== '' && v !== undefined && v !== null) qs.set(k, String(v));
      });
      const url = `${EXPERIMENTS_URL}${def.path}${qs.toString() ? '?' + qs.toString() : ''}`;

      try {
        const resp = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' } });
        const text = await resp.text();
        let data;
        try { data = JSON.parse(text); } catch { data = text; }
        setResult(typeof data === 'string' ? data : JSON.stringify(data, null, 2));
        setResultStatus(resp.ok ? 'success' : 'error');
        if (!resp.ok) toast.error(`${def.title} failed (${resp.status})`);
        else toast.success(`${def.title} completed`);
      } catch (err) {
        setResult(err.message);
        setResultStatus('error');
        toast.error(`${def.title}: ${err.message}`);
      } finally {
        setRunning(false);
      }
    }
  };

  const handleStop = () => { abortRef.current = true; };

  const methodLower = (def.method || 'get').toLowerCase();

  return (
    <div className="action-card">
      <h3>
        <span className={`action-method ${methodLower}`}>{def.method}</span>
        <span className="action-path">{def.path}</span>
      </h3>
      <p className="action-desc">{def.desc}</p>

      {def.params.length > 0 && (
        <div className="action-params">
          {def.params.map(p => (
            <div className="action-param-row" key={p.key}>
              <label>{p.label}</label>
              {p.type === 'bool' ? (
                <select
                  value={String(paramValues[p.key])}
                  onChange={e => handleParamChange(p.key, e.target.value, p.type)}
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input
                  type={p.type === 'number' ? 'number' : 'text'}
                  value={paramValues[p.key] ?? ''}
                  onChange={e => handleParamChange(p.key, e.target.value, p.type)}
                  placeholder={p.key}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="action-buttons">
        <button className="btn-run" onClick={handleRun} disabled={running}>
          {running ? 'Running…' : 'Run'}
        </button>
        {running && def.autoBatch && (
          <button className="btn-stop" onClick={handleStop}>Stop</button>
        )}
      </div>

      {batchLog.length > 0 && (
        <div className="batch-log">
          {batchLog.map((line, i) => <div key={i} className="batch-log-line">{line}</div>)}
        </div>
      )}

      {result !== null && (
        <div className={`action-result ${resultStatus}`}>{result}</div>
      )}
    </div>
  );
}

// ─── DataStore Editor Component ──────────────────────────────────────
function DataStoreEditor() {
  const [tables, setTables] = useState(null);
  const [selectedTable, setSelectedTable] = useState('');
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({}); // { ROWID: { field: newValue, ... } }
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const gridRef = useRef(null);
  // Track known column names per table so we can offer them for new rows
  const columnsRef = useRef([]);

  // Load table list on mount
  React.useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${EXPERIMENTS_URL}/admin/tables`, { credentials: 'include' });
        const data = await resp.json();
        setTables(data.tables || []);
      } catch (err) {
        toast.error('Failed to load table list: ' + err.message);
      }
    })();
  }, []);

  // Load table data
  const loadTable = useCallback(async () => {
    if (!selectedTable) return;
    setLoading(true);
    setPendingChanges({});
    try {
      const resp = await fetch(`${EXPERIMENTS_URL}/admin/table/${selectedTable}`, { credentials: 'include' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const rows = data.rows || [];
      setRowData(rows);

      // Auto-generate columns from keys of the first row
      if (rows.length > 0) {
        const keys = Object.keys(rows[0]);
        // Put ROWID first, then sort the rest alphabetically
        const sorted = keys.filter(k => k !== 'ROWID').sort();
        columnsRef.current = sorted;
        const cols = [
          { headerCheckboxSelection: true, checkboxSelection: true, width: 50, pinned: 'left',
            headerCheckboxSelectionFilteredOnly: true, sortable: false, filter: false, resizable: false },
          { field: 'ROWID', headerName: 'ROWID', editable: false, width: 180, pinned: 'left',
            cellStyle: { color: '#999', fontSize: '12px' } },
          ...sorted.map(k => ({
            field: k,
            headerName: k,
            editable: true,
            minWidth: 120,
            flex: 1,
            cellClassRules: {
              'cell-edited': params => {
                const changes = pendingChangesRef.current;
                return !!(changes[params.data.ROWID] && changes[params.data.ROWID][k] !== undefined);
              }
            }
          }))
        ];
        setColumnDefs(cols);
      } else {
        columnsRef.current = [];
        setColumnDefs([]);
      }

      toast.info(`Loaded ${rows.length} rows from ${selectedTable}`);
    } catch (err) {
      toast.error(`Failed to load ${selectedTable}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedTable]);

  // Keep a ref to pendingChanges for AG Grid cell class rules (avoids stale closure)
  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;

  // Handle cell value change
  const onCellValueChanged = useCallback((event) => {
    const rowId = event.data.ROWID;
    const field = event.colDef.field;
    const newVal = event.newValue;

    setPendingChanges(prev => {
      const existing = prev[rowId] || {};
      return { ...prev, [rowId]: { ...existing, [field]: newVal } };
    });
  }, []);

  // Count pending changes
  const changeCount = Object.keys(pendingChanges).length;

  // Save all changes
  const handleSave = async () => {
    if (changeCount === 0) return;
    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    for (const [rowId, fields] of Object.entries(pendingChanges)) {
      try {
        const resp = await fetch(`${EXPERIMENTS_URL}/admin/table/${selectedTable}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ROWID: rowId, ...fields }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        successCount++;
      } catch (err) {
        console.error(`Failed to update row ${rowId}:`, err);
        errorCount++;
      }
    }

    if (successCount > 0) toast.success(`Saved ${successCount} row(s)`);
    if (errorCount > 0) toast.error(`Failed to save ${errorCount} row(s)`);
    setPendingChanges({});
    setSaving(false);

    // Refresh the grid to clear cell highlights
    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells({ force: true });
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setPendingChanges({});
    loadTable(); // Reload data from server
  };

  // Track selection
  const onSelectionChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    const selected = gridRef.current.api.getSelectedRows();
    setSelectedRows(selected);
  }, []);

  // Delete selected rows
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    const confirmed = window.confirm(`Delete ${selectedRows.length} row(s) from ${selectedTable}?`);
    if (!confirmed) return;

    setDeleting(true);
    let ok = 0, fail = 0;
    for (const row of selectedRows) {
      try {
        const resp = await fetch(`${EXPERIMENTS_URL}/admin/table/${selectedTable}/${row.ROWID}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        ok++;
      } catch (err) {
        console.error(`Failed to delete row ${row.ROWID}:`, err);
        fail++;
      }
    }
    if (ok > 0) toast.success(`Deleted ${ok} row(s)`);
    if (fail > 0) toast.error(`Failed to delete ${fail} row(s)`);
    setDeleting(false);
    setSelectedRows([]);
    loadTable(); // Refresh
  };

  // Add new empty row (insert via API, then reload)
  const handleAddRow = async () => {
    // Build an empty row with all known columns set to empty string
    const emptyRow = {};
    columnsRef.current.forEach(col => { emptyRow[col] = ''; });

    try {
      const resp = await fetch(`${EXPERIMENTS_URL}/admin/table/${selectedTable}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emptyRow),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${resp.status}`);
      }
      toast.success('New row added');
      loadTable(); // Refresh to show the new row
    } catch (err) {
      toast.error(`Failed to add row: ${err.message}`);
    }
  };

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
  }), []);

  return (
    <div>
      <div className="ds-toolbar">
        <select
          value={selectedTable}
          onChange={e => setSelectedTable(e.target.value)}
        >
          <option value="">— Select table —</option>
          {(tables || []).map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <button className="btn-load" onClick={loadTable} disabled={!selectedTable || loading}>
          {loading ? 'Loading…' : 'Load table'}
        </button>

        {selectedTable && rowData.length >= 0 && (
          <button className="btn-load" onClick={handleAddRow} style={{ background: '#28a745' }}>
            + Add row
          </button>
        )}

        {selectedRows.length > 0 && (
          <button className="btn-run danger" onClick={handleDeleteSelected} disabled={deleting}>
            {deleting ? 'Deleting…' : `Delete ${selectedRows.length} row(s)`}
          </button>
        )}

        <input
          className="ds-quick-filter"
          type="text"
          placeholder="Quick filter…"
          value={quickFilter}
          onChange={e => setQuickFilter(e.target.value)}
        />

        {rowData.length > 0 && (
          <span className="ds-info">{rowData.length} rows</span>
        )}

        {changeCount > 0 && (
          <>
            <span className="ds-changes-badge">⚠ {changeCount} unsaved</span>
            <button className="btn-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="btn-discard" onClick={handleDiscard}>
              Discard
            </button>
          </>
        )}
      </div>

      {loading ? (
        <div className="admin-loading">
          <span className="spinner"></span> Loading {selectedTable}…
        </div>
      ) : rowData.length > 0 ? (
        <div className="ds-grid-wrapper ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            modules={[ClientSideRowModelModule]}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onCellValueChanged={onCellValueChanged}
            onSelectionChanged={onSelectionChanged}
            rowSelection="multiple"
            quickFilterText={quickFilter}
            animateRows={true}
            getRowId={params => params.data.ROWID}
            suppressClickEdit={false}
            stopEditingWhenCellsLoseFocus={true}
            suppressRowClickSelection={true}
          />
        </div>
      ) : selectedTable ? (
        <div className="admin-loading" style={{ color: '#bbb' }}>
          Click "Load table" to fetch data
        </div>
      ) : null}
    </div>
  );
}

// ─── Portal State Config Editor ──────────────────────────────────────
const BOOL_FIELDS = ['Header_Visible', 'Detail_Visible', 'Log_Timeline', 'Notify_Customer'];

function BoolCellRenderer(props) {
  return props.value === true || props.value === 'true' ? '✅' : '—';
}

function PortalStateConfigEditor() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const gridRef = useRef(null);
  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;

  const loadData = useCallback(async () => {
    setLoading(true);
    setPendingChanges({});
    try {
      const resp = await fetch(`${EXPERIMENTS_URL}/admin/table/PortalStateConfig`, { credentials: 'include' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const rows = (data.rows || []).map(r => ({
        ...r,
        ...Object.fromEntries(BOOL_FIELDS.map(f => [f, r[f] === true || r[f] === 'true']))
      }));
      setRowData(rows);
      toast.info(`Loaded ${rows.length} Portal State Config rows`);
    } catch (err) {
      toast.error('Failed to load config: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const columnDefs = useMemo(() => [
    { headerCheckboxSelection: true, checkboxSelection: true, width: 50, pinned: 'left',
      headerCheckboxSelectionFilteredOnly: true, sortable: false, filter: false, resizable: false },
    { field: 'ROWID', headerName: 'ROWID', editable: false, width: 100, pinned: 'left',
      cellStyle: { color: '#999', fontSize: '12px' } },
    { field: 'Record_Type', headerName: 'Record Type', editable: true, width: 160, pinned: 'left',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: RECORD_TYPE_OPTIONS },
      cellClassRules: {
        'cell-edited': p => !!(pendingChangesRef.current[p.data.ROWID]?.Record_Type !== undefined),
        'cell-rt-crm-quote': p => p.value === 'CRM Quote',
        'cell-rt-crm-so': p => p.value === 'CRM SO',
        'cell-rt-books-so': p => p.value === 'Books SO',
        'cell-rt-books-invoice': p => p.value === 'Books Invoice',
        'cell-rt-books-retainer': p => p.value === 'Books Retainer',
      }
    },
    { field: 'Source_Stage', headerName: 'Source Stage', editable: true, width: 200,
      cellClassRules: { 'cell-edited': p => !!(pendingChangesRef.current[p.data.ROWID]?.Source_Stage !== undefined) } },
    { field: 'Portal_State', headerName: 'Portal State', editable: true, width: 200,
      cellClassRules: { 'cell-edited': p => !!(pendingChangesRef.current[p.data.ROWID]?.Portal_State !== undefined) } },
    ...['Header_Visible', 'Detail_Visible', 'Log_Timeline', 'Notify_Customer'].map(f => ({
      field: f,
      headerName: f.replace(/_/g, ' '),
      editable: true,
      width: 140,
      cellRenderer: BoolCellRenderer,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: ['true', 'false'] },
      valueSetter: params => {
        const v = params.newValue === 'true' || params.newValue === true;
        if (params.data[f] === v) return false;
        params.data[f] = v;
        return true;
      },
      cellClassRules: { 'cell-edited': p => !!(pendingChangesRef.current[p.data.ROWID]?.[f] !== undefined) }
    })),
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
  }), []);

  const onCellValueChanged = useCallback((event) => {
    const rowId = event.data.ROWID;
    const field = event.colDef.field;
    let newVal = event.newValue;
    if (BOOL_FIELDS.includes(field)) newVal = newVal === true || newVal === 'true';
    setPendingChanges(prev => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [field]: newVal }
    }));
  }, []);

  const changeCount = Object.keys(pendingChanges).length;

  const handleSave = async () => {
    if (changeCount === 0) return;
    setSaving(true);
    let ok = 0, fail = 0;
    for (const [rowId, fields] of Object.entries(pendingChanges)) {
      try {
        const resp = await fetch(`${EXPERIMENTS_URL}/admin/table/PortalStateConfig`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ROWID: rowId, ...fields }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        ok++;
      } catch (err) { fail++; }
    }
    if (ok > 0) toast.success(`Saved ${ok} row(s)`);
    if (fail > 0) toast.error(`Failed to save ${fail} row(s)`);
    setPendingChanges({});
    setSaving(false);
    if (gridRef.current?.api) gridRef.current.api.refreshCells({ force: true });
  };

  const handleDiscard = () => { setPendingChanges({}); loadData(); };

  const onSelectionChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    setSelectedRows(gridRef.current.api.getSelectedRows());
  }, []);

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`Delete ${selectedRows.length} row(s)?`)) return;
    setDeleting(true);
    let ok = 0, fail = 0;
    for (const row of selectedRows) {
      try {
        const resp = await fetch(`${EXPERIMENTS_URL}/admin/table/PortalStateConfig/${row.ROWID}`, {
          method: 'DELETE', credentials: 'include',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        ok++;
      } catch (err) { fail++; }
    }
    if (ok > 0) toast.success(`Deleted ${ok} row(s)`);
    if (fail > 0) toast.error(`Failed to delete ${fail} row(s)`);
    setDeleting(false);
    setSelectedRows([]);
    loadData();
  };

  const handleAddRow = async () => {
    const emptyRow = {
      Record_Type: 'CRM Quote', Source_Stage: '', Portal_State: '',
      Header_Visible: false, Detail_Visible: false, Log_Timeline: false, Notify_Customer: false,
    };
    try {
      const resp = await fetch(`${EXPERIMENTS_URL}/admin/table/PortalStateConfig`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emptyRow),
      });
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error || `HTTP ${resp.status}`);
      toast.success('New row added');
      loadData();
    } catch (err) { toast.error(`Failed to add row: ${err.message}`); }
  };

  const handleReseed = async () => {
    if (!window.confirm('This will DELETE all rows and re-seed with defaults. Continue?')) return;
    try {
      const resp = await fetch(`${EXPERIMENTS_URL}/portal-state-config/seed`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      toast.success(data.message || `Seeded ${data.inserted} rows`);
      loadData();
    } catch (err) { toast.error('Seed failed: ' + err.message); }
  };

  return (
    <div>
      <div className="ds-toolbar">
        <button className="btn-load" onClick={loadData} disabled={loading}>
          {loading ? 'Loading…' : '🔄 Reload'}
        </button>
        <button className="btn-load" onClick={handleAddRow} style={{ background: '#28a745' }}>
          + Add row
        </button>
        <button className="btn-load" onClick={handleReseed} style={{ background: '#e67e22' }}>
          🌱 Re-seed defaults
        </button>

        {selectedRows.length > 0 && (
          <button className="btn-run danger" onClick={handleDeleteSelected} disabled={deleting}>
            {deleting ? 'Deleting…' : `Delete ${selectedRows.length} row(s)`}
          </button>
        )}

        <input className="ds-quick-filter" type="text" placeholder="Quick filter…"
          value={quickFilter} onChange={e => setQuickFilter(e.target.value)} />

        {rowData.length > 0 && <span className="ds-info">{rowData.length} rows</span>}

        {changeCount > 0 && (
          <>
            <span className="ds-changes-badge">⚠ {changeCount} unsaved</span>
            <button className="btn-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="btn-discard" onClick={handleDiscard}>Discard</button>
          </>
        )}
      </div>

      {loading ? (
        <div className="admin-loading"><span className="spinner"></span> Loading Portal State Config…</div>
      ) : rowData.length > 0 ? (
        <div className="ds-grid-wrapper ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            modules={[ClientSideRowModelModule]}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onCellValueChanged={onCellValueChanged}
            onSelectionChanged={onSelectionChanged}
            rowSelection="multiple"
            quickFilterText={quickFilter}
            animateRows={true}
            getRowId={params => params.data.ROWID}
            stopEditingWhenCellsLoseFocus={true}
            suppressRowClickSelection={true}
          />
        </div>
      ) : (
        <div className="admin-loading" style={{ color: '#bbb' }}>No data — click Reload or Re-seed</div>
      )}
    </div>
  );
}

// ─── Record Inspector Component ──────────────────────────────────────
const INSPECTOR_MODULES = [
  { value: 'Contacts',        label: 'CRM — Contact',        type: 'crm' },
  { value: 'Accounts',        label: 'CRM — Account',        type: 'crm' },
  { value: 'Quotes',          label: 'CRM — Quote',          type: 'crm' },
  { value: 'Sales_Orders',    label: 'CRM — Sales Order',    type: 'crm' },
  { value: 'Deals',           label: 'CRM — Deal',           type: 'crm' },
  { value: 'salesorders',     label: 'Books — Sales Order',  type: 'books' },
  { value: 'invoices',        label: 'Books — Invoice',      type: 'books' },
  { value: 'retainerinvoices',label: 'Books — Retainer Invoice', type: 'books' },
];

function RecordInspector() {
  const [module, setModule] = useState(INSPECTOR_MODULES[0].value);
  const [recordId, setRecordId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleInspect = async () => {
    const id = recordId.trim();
    if (!id) { toast.warn('Enter a record ID'); return; }
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const resp = await fetch(`${EXPERIMENTS_URL}/inspect/${module}/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      const body = await resp.json();
      if (!resp.ok) {
        setError(body);
        toast.error(`Error ${resp.status}`);
      } else {
        setResult(body);
        toast.success(`Loaded ${module} / ${id}`);
      }
    } catch (err) {
      setError({ error: err.message });
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleInspect();
  };

  const json = result || error;

  return (
    <div className="inspector-panel">
      <div className="inspector-controls">
        <select
          className="inspector-select"
          value={module}
          onChange={e => setModule(e.target.value)}
        >
          {INSPECTOR_MODULES.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <input
          className="inspector-input"
          type="text"
          placeholder="Record ID"
          value={recordId}
          onChange={e => setRecordId(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button className="btn-run" onClick={handleInspect} disabled={loading}>
          {loading ? 'Loading…' : '🔍 Inspect'}
        </button>
      </div>
      {json && (
        <pre className="inspector-json">{JSON.stringify(json, null, 2)}</pre>
      )}
    </div>
  );
}

// ─── Main Admin Dashboard ────────────────────────────────────────────
function AdminDashboard() {
  const [tab, setTab] = useState('actions');
  const navigate = useNavigate();

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-header-left">
          <button className="btn-back" onClick={() => navigate('/')}>← Home</button>
          <h1>⚙️ Admin Dashboard</h1>
        </div>
      </header>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${tab === 'actions' ? 'active' : ''}`}
          onClick={() => setTab('actions')}
        >
          🚀 Actions
        </button>
        <button
          className={`admin-tab ${tab === 'datastore' ? 'active' : ''}`}
          onClick={() => setTab('datastore')}
        >
          🗄️ DataStore Editor
        </button>
        <button
          className={`admin-tab ${tab === 'portalstate' ? 'active' : ''}`}
          onClick={() => setTab('portalstate')}
        >
          📋 Portal State Config
        </button>
        <button
          className={`admin-tab ${tab === 'inspector' ? 'active' : ''}`}
          onClick={() => setTab('inspector')}
        >
          🔍 Record Inspector
        </button>
      </div>

      <div className="admin-content">
        {tab === 'actions' && (
          <div className="actions-grid">
            {ACTION_DEFS.map(def => (
              <ActionCard key={def.id} def={def} />
            ))}
          </div>
        )}

        {tab === 'datastore' && (
          <DataStoreEditor />
        )}

        {tab === 'portalstate' && (
          <PortalStateConfigEditor />
        )}

        {tab === 'inspector' && (
          <RecordInspector />
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
